import React from "react";
import { AlertCard } from "@truevault/aquilo";
import { PublicDomainSetupInstructionsDto } from "../../../common/service/server/dto/PublicDomainSetupInstructionsDto";
import {
  DomainSetupDnsRecordsTable,
  DomainSetupDocumentationLink,
  DomainSetupInlineCopyField,
  DomainSetupStepSection,
} from "../superorg/settings/DomainSetupInstructionPrimitives";

type PublicDomainSetupInstructionsProps = {
  instructions: PublicDomainSetupInstructionsDto;
};

export function PublicDomainSetupInstructions({
  instructions,
}: PublicDomainSetupInstructionsProps) {
  const dnsRecords = React.useMemo(
    () => instructions.dnsRecords.map((record, index) => ({ ...record, id: String(index) })),
    [instructions.dnsRecords],
  );
  const usesOwnedPostmarkMailbox = instructions.mode === "OWNED_POSTMARK";
  const usesLegacyMailbox = instructions.mode === "LEGACY";
  const dnsStepNumber = usesOwnedPostmarkMailbox ? "3" : "2";

  return (
    <section id="email-domain-setup" className="tw-boundary mb-[32px]">
      <div className="rounded-[12px] border border-solid border-neutral-300 bg-white px-[24px] py-[24px]">
        <h2 className="m-0 text-[20px] font-semibold leading-[30px] text-primary-800">
          Privacy Email &amp; Domain Setup
        </h2>
        <p className="mb-0 mt-[8px] text-[14px] leading-[21px] text-neutral-600">
          Review the configured privacy email details and complete the domain steps shown below. You
          do not need a TrueVault account to use these instructions.
        </p>

        {!instructions.ready ? (
          <AlertCard
            variant="warning"
            title="These setup instructions are not ready yet."
            description="Ask your TrueVault administrator to finish saving the privacy email and domain configuration, then refresh this page."
            containerClasses="mt-[20px] mb-0"
          />
        ) : (
          <div className="mt-[24px]">
            <DomainSetupStepSection number="1" title="Review the configured setup details">
              <div className="max-w-[720px] space-y-[12px]">
                {instructions.primaryDomain && (
                  <DomainSetupInlineCopyField
                    label="Primary Domain:"
                    value={instructions.primaryDomain}
                  />
                )}
                {instructions.privacyDomain && (
                  <DomainSetupInlineCopyField
                    label="Privacy Center Domain:"
                    value={instructions.privacyDomain}
                  />
                )}
                {usesLegacyMailbox && instructions.privacyEmailAddress && (
                  <DomainSetupInlineCopyField
                    label="Privacy Email:"
                    value={instructions.privacyEmailAddress}
                  />
                )}
              </div>
              {instructions.mode === "SHARED_POSTMARK" && (
                <AlertCard
                  variant="info"
                  title="Privacy email setup is managed by another organization."
                  description="Only the Privacy Center domain record below needs to be added for this organization."
                  containerClasses="mt-[12px] mb-0"
                />
              )}
              {usesLegacyMailbox && (
                <AlertCard
                  variant="info"
                  title="The privacy email is already connected."
                  description="No email forwarding or email DNS changes are required here. Add only the custom Privacy Center domain record below."
                  containerClasses="mt-[12px] mb-0"
                />
              )}
            </DomainSetupStepSection>

            {usesOwnedPostmarkMailbox &&
              instructions.privacyEmailAddress &&
              instructions.forwardingAddress && (
                <DomainSetupStepSection
                  number="2"
                  title="Set up email forwarding"
                  subtitle={
                    <div className="space-y-[8px]">
                      <div>
                        Using Google Workspace?{" "}
                        <DomainSetupDocumentationLink href="https://developers.truevault.com/docs/email-setup/google-workspace-default-routing">
                          Follow these instructions
                        </DomainSetupDocumentationLink>
                      </div>
                      <div>
                        Using Microsoft 365?{" "}
                        <DomainSetupDocumentationLink href="https://developers.truevault.com/docs/email-setup/microsoft-365-groups">
                          Follow these instructions
                        </DomainSetupDocumentationLink>
                      </div>
                    </div>
                  }
                >
                  <ol className="custom custom-sublist domain-setup-step-list flex w-full flex-col gap-[12px] p-0 pl-[24px]">
                    <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                      <div className="flex-1">Sign in to the email provider’s admin console.</div>
                    </li>
                    <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                      <div className="flex-1">
                        Create or update the forwarding rule using the details below.
                      </div>
                    </li>
                    <li className="flex w-full items-start">
                      <DomainSetupInlineCopyField
                        label="Your Address:"
                        value={instructions.privacyEmailAddress}
                      />
                    </li>
                    <li className="flex w-full items-start">
                      <DomainSetupInlineCopyField
                        label="Forward to:"
                        value={instructions.forwardingAddress}
                      />
                    </li>
                    <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                      <div className="flex-1">
                        Click <span className="font-bold">Save.</span>
                      </div>
                    </li>
                  </ol>
                </DomainSetupStepSection>
              )}

            <DomainSetupStepSection
              number={dnsStepNumber}
              title={
                usesOwnedPostmarkMailbox
                  ? "Add DNS records for the privacy email and custom Privacy Center domain"
                  : "Add the custom Privacy Center domain record"
              }
              subtitle={
                <span>
                  First time adding DNS records?{" "}
                  <DomainSetupDocumentationLink href="https://developers.truevault.com/docs/domain-setup/dns-record-configuration">
                    Here's a how-to guide.
                  </DomainSetupDocumentationLink>
                </span>
              }
            >
              <ol className="custom custom-sublist domain-setup-step-list flex w-full flex-col gap-[12px] p-0 pl-[24px]">
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="flex-1">Sign in to the domain host’s admin console.</div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="flex-1">
                    Open the DNS or DNS Records settings for the primary domain.
                  </div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="min-w-0 flex-1">
                    Click <span className="font-bold">Add New Record</span> and add each record
                    below.
                    <div className="mt-[12px] overflow-x-auto px-[8px]">
                      <DomainSetupDnsRecordsTable records={dnsRecords} />
                    </div>
                  </div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="flex-1">
                    Click <span className="font-bold">Save.</span>
                  </div>
                </li>
              </ol>
            </DomainSetupStepSection>

            <AlertCard
              variant="info"
              title="Finished? Ask your TrueVault administrator to verify the setup."
              description="Verification and test-email controls remain available only inside TrueVault."
              containerClasses="ml-[40px] mt-[24px] mb-0"
            />
          </div>
        )}
      </div>
    </section>
  );
}
